import * as React from "react";
import { observer, inject } from "mobx-react";
import { QueryRoute } from "bernie-core";
import { Layer } from "bernie-view";
import { TypeaheadSelection } from "uitk-react-typeahead";
import { LocationProps } from "./typings";
import { LocationField } from "../LocationField/LocationField";
import { FlightWizardState } from "stores/wizard/state";
import { SwapLocations } from "../SwapLocations/SwapLocations";
import { UitkLayoutGrid, UitkLayoutGridItem } from "uitk-react-layout-grid";
import { useLocalization } from "@shared-ui/localization-context";

export const Location: React.FunctionComponent<LocationProps> = inject(
  "context",
  "routeStore"
)(
  observer(
    ({
      lobState,
      context,
      routeStore,
      originField,
      destinationField,
      colSpan,
      multiLegIndex,
      showSwapLocationsControl,
    }) => {
      if (!context || !routeStore) {
        // Check for injected stores
        return null;
      }

      const locationConfig =
        multiLegIndex !== undefined && lobState instanceof FlightWizardState && lobState.config.multiCityFlight
          ? lobState.config.multiCityFlight.legs[multiLegIndex].location
          : lobState.tabSelected === false
          ? lobState.config.gtConfig.location
          : lobState.config.location;
      const locationState =
        multiLegIndex !== undefined && lobState instanceof FlightWizardState && lobState.listOfAdditionalLegs
          ? lobState.listOfAdditionalLegs[multiLegIndex].location
          : lobState.location;

      const { formatText } = useLocalization();

      // TODO: Consolidate functions doing related logic.

      const onOriginLocationFieldUpdate = (selection: TypeaheadSelection) => {
        if (lobState.updateOriginSelection) {
          lobState.updateOriginSelection(selection, multiLegIndex);
        }
      };

      const onDestinationLocationFieldUpdate = (selection: TypeaheadSelection) => {
        lobState.updateDestinationSelection(selection, multiLegIndex);
      };

      const openOriginTypeaheadDialog = () => {
        const pwaDialog = (locationConfig.origin && locationConfig.origin.pwaDialogQueryParam) || "default";
        routeStore.updateSearch({ pwaDialog });
      };

      const openDestinationTypeaheadDialog = () => {
        routeStore.updateSearch({
          pwaDialog: locationConfig.destination.pwaDialogQueryParam,
        });
      };

      const originTypeaheadDialog = (children: JSX.Element) => {
        const pwaDialog = (locationConfig.origin && locationConfig.origin.pwaDialogQueryParam) || "default";

        return (
          <QueryRoute query={{ pwaDialog }}>
            <Layer id="typeahead">{children}</Layer>
          </QueryRoute>
        );
      };

      const swapOriginAndDestination = () => {
        if (lobState.swapLocations) {
          lobState.swapLocations();
        }
      };

      const destinationTypeaheadDialog = (children: JSX.Element) => {
        return (
          <QueryRoute query={{ pwaDialog: locationConfig.destination.pwaDialogQueryParam }}>
            <Layer id="typeahead">{children}</Layer>
          </QueryRoute>
        );
      };

      const closeDialog = () => {
        routeStore.goBack();
      };

      const originInvalidMessage = lobState.originInvalidKey ? formatText(lobState.originInvalidKey) : "";
      const destinationInvalidMessage = lobState.destinationInvalidKey
        ? formatText(lobState.destinationInvalidKey)
        : "";

      const locationClassWithSwap = showSwapLocationsControl ? "locationWithSwap" : "";
      const locationFieldCols = originField && destinationField ? 1 : 2;

      return (
        <UitkLayoutGridItem colSpan={colSpan}>
          <div>
            <UitkLayoutGrid
              columns={{ small: 1, medium: 2 }}
              space="three"
              className={`Location ${locationClassWithSwap}`}
            >
              {originField && locationConfig.origin && (
                <UitkLayoutGridItem colSpan={{ small: 1, medium: locationFieldCols }}>
                  <div>
                    <LocationField
                      context={context}
                      onClick={openOriginTypeaheadDialog}
                      onDismiss={closeDialog}
                      onUpdate={onOriginLocationFieldUpdate}
                      renderDialog={originTypeaheadDialog}
                      icon={locationConfig.origin.icon}
                      name={lobState instanceof FlightWizardState ? "" : locationConfig.origin.inputName}
                      label={formatText(locationConfig.origin.labelToken)}
                      typeaheadPlaceholder={formatText(locationConfig.origin.typeaheadLabelToken)}
                      invalid={originInvalidMessage}
                      value={locationState.origin.value}
                      isDest={false}
                      essAdapterConfig={locationConfig.origin.essAdapterConfig}
                      stid={`location-field-${locationConfig.origin.inputName}`}
                      config={locationConfig.origin}
                      location={locationState.origin}
                    />
                  </div>
                </UitkLayoutGridItem>
              )}
              {showSwapLocationsControl && <SwapLocations swapLocations={swapOriginAndDestination} />}
              {destinationField && locationConfig.destination && (
                <UitkLayoutGridItem colSpan={{ small: 1, medium: locationFieldCols }}>
                  <div>
                    <LocationField
                      context={context}
                      onClick={openDestinationTypeaheadDialog}
                      onDismiss={closeDialog}
                      onUpdate={onDestinationLocationFieldUpdate}
                      renderDialog={destinationTypeaheadDialog}
                      icon={locationConfig.destination.icon}
                      name={lobState instanceof FlightWizardState ? "" : locationConfig.destination.inputName}
                      label={formatText(locationConfig.destination.labelToken)}
                      typeaheadPlaceholder={formatText(locationConfig.destination.typeaheadLabelToken)}
                      invalid={destinationInvalidMessage}
                      value={locationState.destination.value}
                      isDest={true}
                      essAdapterConfig={locationConfig.destination.essAdapterConfig}
                      stid={`location-field-${locationConfig.destination.inputName}`}
                      config={locationConfig.destination}
                      location={locationState.destination}
                    />
                  </div>
                </UitkLayoutGridItem>
              )}
            </UitkLayoutGrid>
          </div>
        </UitkLayoutGridItem>
      );
    }
  )
);
